import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../components/studio/studioToast';
import jsPDF from 'jspdf';
import { AlignCenter, Image as ImageIcon, Omega, Pencil, Sparkles, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PageLoader } from '../components/Spinner';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';
import { API_URL } from '../utils/api';
import { EDITOR_FONTS, loadEditorFonts, loadFontStylesheets } from '../utils/fonts';
import { copyToClipboard, formatPrice, getTemplatePrice } from '../utils/format';
import { getErrorMessage } from '../utils/errors';
import { useDocumentTitle } from '../hooks/use-document-title';
import { cn } from '../lib/utils';
import { PAGE_HEIGHT, PAGE_WIDTH } from '../utils/invitationStyle';
import { restackText } from '../utils/restack';
import { useDesignHistory } from '../components/studio/useDesignHistory';
import {
  addElements,
  addText,
  applyTheme,
  cleanForSave,
  duplicateItem,
  fieldsFromTemplate,
  findItem,
  isDeletable,
  itemLabel,
  moveLayer,
  removeItem,
  replaceColor,
  snapshot,
  updateItem,
  clamp,
  round,
} from '../components/studio/designUtils';
import { FOCUS_RING, MAX_ELEMENTS, MAX_TEXT_FIELDS, MODE_STORAGE_KEY } from '../components/studio/constants';
import { StudioTopBar } from '../components/studio/StudioTopBar';
import { StudioCanvas } from '../components/studio/StudioCanvas';
import { ToolRail, PhoneTabBar, QuickChip } from '../components/studio/StudioTools';
import { ElementProperties, NothingSelected, TextProperties } from '../components/studio/PropertiesPanel';
import { SimplePanel } from '../components/studio/SimplePanel';
import { PreviewDialog, PurchaseDialog, ShortcutsDialog, VersionsDialog } from '../components/studio/StudioDialogs';
import { TextPanel } from '../components/studio/panels/TextPanel';
import { ColorsPanel } from '../components/studio/panels/ColorsPanel';
import { AiPanel } from '../components/studio/panels/AiPanel';
import { GlyphsPanel } from '../components/studio/panels/GlyphsPanel';
import { ElementsPanel } from '../components/studio/panels/ElementsPanel';
import { UploadsPanel } from '../components/studio/panels/UploadsPanel';
import { LayersPanel } from '../components/studio/panels/LayersPanel';
import { TextDesignsPanel } from '../components/studio/panels/TextDesignsPanel';
import { BackgroundPanel } from '../components/studio/panels/BackgroundPanel';
import { BackgroundTintControls } from '../components/studio/panels/BackgroundTintControls';
import { BackgroundAdjustControls, BG_ADJUST_KEYS, hasBackgroundAdjust } from '../components/studio/panels/BackgroundAdjustControls';
import { assetUrl } from '../components/design/DesignLayers';
import { ELEMENT_ART_PREFIX } from '../components/studio/elementArt';
import { fontsForBackground, refontForBackground, textDesignsFor } from '../components/studio/fontScope';

const EMPTY = { text_fields: [], elements: [], template_id: null, background: null };
const PHONE_QUERY = '(max-width: 1023px)';

const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const update = () => setIsPhone(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isPhone;
};

const readMode = () => {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === 'simple' || stored === 'advanced') return stored;
  } catch (e) {
    // storage unavailable
  }
  return window.matchMedia(PHONE_QUERY).matches ? 'simple' : 'advanced';
};

const isEditableTarget = (el) =>
  el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

/** Convert a downloaded blob to another image type via a canvas. */
const convertBlob = (blob, mime) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (mime === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((out) => (out ? resolve(out) : reject(new Error('convert failed'))), mime, 0.95);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the generated image'));
    };
    img.src = url;
  });

export default function EditorPage() {
  const { templateId, invitationId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const isPhone = useIsPhone();
  const isAdmin = Boolean(user?.is_admin);

  const [template, setTemplate] = useState(null); // template the design was loaded with
  const [templatesById, setTemplatesById] = useState({});
  const [categoryTemplates, setCategoryTemplates] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminFonts, setAdminFonts] = useState([]);
  const [canDownload, setCanDownload] = useState(false);
  const [name, setName] = useState('My Invitation');
  const [saved, setSaved] = useState({ snap: snapshot(EMPTY), name: 'My Invitation', exists: false });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  const { design, designRef, change, undo, redo, reset, breakMerge, canUndo, canRedo } = useDesignHistory(EMPTY);
  // Selection: one or more items. Single-item UI (properties, selection bar) uses `selectedId`.
  const [selection, setSelection] = useState([]);
  const selectedId = selection.length === 1 ? selection[0] : null;
  const setSelectedId = useCallback((id) => setSelection(id ? [id] : []), []);
  const clipboard = useRef([]); // copied items (Ctrl+C / Ctrl+V)
  const caretRef = useRef(null); // { id, start, end } of the text box being typed in (glyph insertion)
  const [caret, setCaret] = useState(null); // same, as state: shows "alternates for the selected letter"
  const reportCaret = useCallback((id, start, end) => {
    caretRef.current = { id, start, end };
    setCaret((prev) => (prev && prev.id === id && prev.start === start && prev.end === end ? prev : { id, start, end }));
  }, []);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [mode, setModeState] = useState(readMode);
  const [tab, setTab] = useState('text');
  const [themeId, setThemeId] = useState(null);
  const [textDesignId, setTextDesignId] = useState(null); // Ready text: the design last applied
  const [simpleStep, setSimpleStep] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [focusTick, setFocusTick] = useState(0); // Simple mode: focus the input of a clicked line

  const [versionsOpen, setVersionsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [purchase, setPurchase] = useState(null); // null | { reason }
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [phoneSheet, setPhoneSheet] = useState(null); // tool tab id
  const [phoneQuick, setPhoneQuick] = useState(null); // properties section

  const loadedId = useRef(null);

  useDocumentTitle(invitation ? `Editing ${name || 'invitation'}` : template ? `Customize ${template.name}` : 'Editor');

  // ---- Fonts ----
  useEffect(() => {
    loadEditorFonts();
    axios
      .get(`${API_URL}/fonts`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setAdminFonts(list);
        loadFontStylesheets(list);
      })
      .catch(() => {
        // Optional: the built-in fonts still work.
      });
  }, []);

  const siteFonts = useMemo(() => {
    const builtIn = EDITOR_FONTS.map((f) => ({ family: f.family, label: f.family, hebrew: f.hebrew, decorative: f.decorative }));
    const known = new Set(builtIn.map((f) => f.family));
    const extra = adminFonts
      .filter((f) => f?.font_family && !known.has(f.font_family) && known.add(f.font_family))
      .map((f) => ({ family: f.font_family, label: f.name || f.font_family, uploaded: true }));
    return [...builtIn, ...extra];
  }, [adminFonts]);

  // ---- Loading ----
  useEffect(() => {
    let cancelled = false;

    const loadTemplate = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/templates/${templateId}`);
        if (cancelled) return;
        setTemplate(data);
        setTemplatesById((m) => ({ ...m, [data.id]: data }));
        const initial = {
          text_fields: fieldsFromTemplate(data.text_fields),
          elements: Array.isArray(data.elements) ? data.elements : [],
          template_id: data.id,
          background: null,
        };
        reset(initial);
        setSaved({ snap: snapshot(initial), name: 'My Invitation', exists: false });
      } catch (error) {
        if (cancelled) return;
        toast.error(getErrorMessage(error, 'We could not load this template.'));
        navigate('/templates', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const loadInvitation = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/invitations/${invitationId}`);
        if (cancelled) return;
        setInvitation(data);
        setName(data.name || 'My Invitation');
        const [status, tmpl] = await Promise.all([
          axios.get(`${API_URL}/invitations/${invitationId}/download-status`),
          axios.get(`${API_URL}/templates/${data.template_id}`),
        ]);
        if (cancelled) return;
        setCanDownload(Boolean(status.data.can_download));
        setTemplate(tmpl.data);
        setTemplatesById((m) => ({ ...m, [tmpl.data.id]: tmpl.data }));
        // Saved invitations may not store field labels; restore them from the template.
        const labels = Object.fromEntries((tmpl.data.text_fields || []).map((tf) => [tf.id, tf.label]));
        // fixed choices (Simcha Day) live on the template only: give them back to the saved lines
        const choices = Object.fromEntries((tmpl.data.text_fields || []).filter((tf) => tf.options?.length).map((tf) => [tf.id, tf.options]));
        const loaded = {
          text_fields: (data.text_fields || [])
            .map((tf) => (tf.label || !labels[tf.id] ? tf : { ...tf, label: labels[tf.id] }))
            .map((tf) => (choices[tf.id] ? { ...tf, options: choices[tf.id] } : tf)),
          elements: Array.isArray(data.elements) ? data.elements : [],
          template_id: data.template_id,
          background: data.background && (data.background.tint_color || hasBackgroundAdjust(data.background)) ? data.background : null,
        };
        reset(loaded);
        setSaved({ snap: snapshot(loaded), name: data.name || 'My Invitation', exists: true });
        loadedId.current = data.id;
      } catch (error) {
        if (cancelled) return;
        toast.error(getErrorMessage(error, 'We could not load this invitation.'));
        navigate('/dashboard', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (invitationId) {
      // Just created this invitation (POST then navigate): nothing to reload.
      if (loadedId.current === invitationId) return undefined;
      loadInvitation();
    } else if (templateId) {
      loadTemplate();
    }
    return () => {
      cancelled = true;
    };
  }, [templateId, invitationId, navigate, reset]);

  // Other designs in the same category (Background tool).
  const categoryId = template?.category_id;
  useEffect(() => {
    if (!categoryId) return undefined;
    let cancelled = false;
    setCategoryLoading(true);
    axios
      .get(`${API_URL}/templates`, { params: { category_id: categoryId } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data.filter((t) => t && t.id) : [];
        setCategoryTemplates(list);
        setTemplatesById((m) => ({ ...Object.fromEntries(list.map((t) => [t.id, t])), ...m }));
      })
      .catch(() => {
        if (!cancelled) setCategoryTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setCategoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  // Make sure the template of the current design is known (e.g. after restoring a version).
  useEffect(() => {
    const id = design.template_id;
    if (!id || templatesById[id]) return;
    axios
      .get(`${API_URL}/templates/${id}`)
      .then(({ data }) => data?.id && setTemplatesById((m) => ({ ...m, [data.id]: data })))
      .catch(() => {});
  }, [design.template_id, templatesById]);

  const currentTemplate = templatesById[design.template_id] || template;

  // A partner template's own fonts are not in the site-wide /fonts list: load and offer them for this template.
  useEffect(() => {
    const extra = Array.isArray(currentTemplate?.fonts) ? currentTemplate.fonts : [];
    const missing = extra.filter((f) => f?.font_family && !adminFonts.some((x) => x.font_family === f.font_family));
    if (!missing.length) return;
    loadFontStylesheets(missing);
    setAdminFonts((list) => [...list, ...missing]);
  }, [currentTemplate, adminFonts]);
  // Fonts belong to the background's owner: a partner's fonts only on its backgrounds (fontScope.js).
  const fonts = useMemo(() => fontsForBackground(siteFonts, currentTemplate), [siteFonts, currentTemplate]);

  const dirty = snapshot(design) !== saved.snap || name !== saved.name;
  const saveState = saving ? 'saving' : dirty ? (saved.exists ? 'unsaved' : 'new') : saved.exists ? 'saved' : 'new';

  // Warn before leaving the page with unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Phone: close the quick controls when the selection changes.
  useEffect(() => {
    setPhoneQuick(null);
  }, [selectedId]);

  // Drop the selection if the item disappears (undo, delete, restore).
  useEffect(() => {
    setSelection((sel) => {
      const next = sel.filter((id) => findItem(design, id));
      return next.length === sel.length ? sel : next;
    });
  }, [design]);

  const setMode = (next) => {
    setModeState(next);
    setPhoneQuick(null);
    setPhoneSheet(null);
    try {
      localStorage.setItem(MODE_STORAGE_KEY, next);
    } catch (e) {
      // ignore
    }
  };

  // ---- Design actions ----
  const selected = findItem(design, selectedId);
  const closePhoneSheet = () => isPhone && setPhoneSheet(null);

  const patchItem = useCallback(
    (id, patch, key) => change((d) => updateItem(d, id, patch), key ? { key } : undefined),
    [change]
  );

  const patchSelected = (patch, key) => selectedId && patchItem(selectedId, patch, key);

  const handleAddText = (field) => {
    if (designRef.current.text_fields.length >= MAX_TEXT_FIELDS) {
      toast.error(`An invitation can have up to ${MAX_TEXT_FIELDS} text boxes.`);
      return;
    }
    let createdId;
    change((d) => {
      const r = addText(d, field);
      createdId = r.id;
      return r.design;
    });
    setSelectedId(createdId);
    closePhoneSheet();
  };

  const handleApplyPreset = (preset) => {
    if (selected?.kind === 'text') {
      patchSelected(preset.patch);
    } else {
      handleAddText({ ...preset.patch, text: preset.sample, font_size: preset.size || 56, label: preset.name });
    }
    closePhoneSheet();
  };

  const handleAddElements = (items) => {
    if (designRef.current.elements.length + items.length > MAX_ELEMENTS) {
      toast.error(`An invitation can have up to ${MAX_ELEMENTS} shapes, lines and photos.`);
      return;
    }
    let createdId;
    change((d) => {
      const r = addElements(d, items);
      createdId = r.id;
      return r.design;
    });
    setSelectedId(createdId);
    closePhoneSheet();
  };

  const handlePlacePhoto = (src) => {
    const img = new window.Image();
    const place = (ratio) => {
      const w = 40;
      const h = clamp(round(((w * PAGE_WIDTH) / PAGE_HEIGHT) * ratio), 2, 95);
      handleAddElements([{ type: 'image', src, w, h, fit: 'cover', radius: 0 }]);
    };
    img.onload = () => place(img.naturalWidth ? img.naturalHeight / img.naturalWidth : 1);
    img.onerror = () => place(1);
    img.src = assetUrl(src);
  };

  const handleDuplicate = () => {
    if (!selectedId) return;
    const isText = selected?.kind === 'text';
    if (isText && design.text_fields.length >= MAX_TEXT_FIELDS) return toast.error('Too many text boxes.');
    if (!isText && design.elements.length >= MAX_ELEMENTS) return toast.error('Too many elements.');
    let createdId;
    change((d) => {
      const r = duplicateItem(d, selectedId);
      createdId = r.id;
      return r.design;
    });
    setSelectedId(createdId);
    return undefined;
  };

  const handleRemove = (id = selectedId) => {
    const found = findItem(designRef.current, id);
    if (!found) return;
    if (isDeletable(found)) {
      change((d) => removeItem(d, id));
      if (id === selectedId) setSelectedId(null);
    } else {
      patchItem(id, { hidden: true });
      toast.info(`“${itemLabel(found)}” is hidden. Template lines can be hidden but not deleted; show it again from Layers.`);
    }
  };

  const handleMove = (id, dir) => change((d) => moveLayer(d, id, dir));

  const handleApplyTheme = (theme) => {
    change((d) => applyTheme(d, theme));
    setThemeId(theme.id);
    setSimpleStep((s) => Math.max(s, 1));
  };

  const designLockedReason =
    invitation?.is_paid && !isAdmin ? 'This invitation has been purchased, so its design can no longer be changed.' : '';

  const handlePickTemplate = (t) => {
    if (designLockedReason) {
      toast.info(designLockedReason);
      return;
    }
    setTemplatesById((m) => ({ ...m, [t.id]: t }));
    setTextDesignId(null);
    // Fonts the new background doesn't allow (another partner's, or Azamein's on a partner background)
    // are swapped for that background's own fonts, in the same undo step.
    const allowedFonts = new Set(fontsForBackground(siteFonts, t).map((f) => f.family));
    const refont = (fields) => refontForBackground(fields, t, allowedFonts).fields;
    const refonted = refontForBackground(design.text_fields, t, allowedFonts).changed;
    // Each background has its own text space (template.text_area, % of the page). Move and resize the
    // customer's text and shapes from the old space into the new one, so a design laid out for a small
    // oval still fits a wide frame (and the other way round). One undo step.
    const from = currentTemplate?.text_area;
    const to = t.text_area;
    const refit = from && to && ['x0', 'x1', 'y0', 'y1'].some((k) => Math.abs((from[k] ?? 0) - (to[k] ?? 0)) > 0.5);
    change((d) => {
      if (!refit) return { ...d, template_id: t.id, text_fields: refont(d.text_fields) };
      const sx = (to.x1 - to.x0) / Math.max(1, from.x1 - from.x0);
      const sy = (to.y1 - to.y0) / Math.max(1, from.y1 - from.y0);
      const k = Math.min(sx, sy);
      const mx = (x) => round(clamp(to.x0 + ((x ?? 50) - from.x0) * sx, 0, 100));
      const my = (y) => round(clamp(to.y0 + ((y ?? 50) - from.y0) * sy, 0, 100));
      return {
        ...d,
        template_id: t.id,
        text_fields: refont(d.text_fields).map((f) => ({
          ...f,
          x: mx(f.x),
          y: my(f.y),
          font_size: clamp(Math.round((f.font_size || 24) * k), 4, 400),
          ...(f.line_width ? { line_width: round(clamp(f.line_width * sx, 3, 100), 1) } : {}),
        })),
        elements: d.elements.map((e) => ({
          ...e,
          x: mx(e.x),
          y: my(e.y),
          ...(e.w ? { w: round(e.w * sx) } : {}),
          ...(e.h ? { h: round(e.h * sy) } : {}),
        })),
      };
    });
    const oldPrice = getTemplatePrice(currentTemplate);
    const newPrice = getTemplatePrice(t);
    toast.success(`Design changed to “${t.name}”${newPrice !== oldPrice ? ` · ${formatPrice(newPrice)}` : ''}${refit ? ' · text fitted to the new design (Ctrl+Z to undo)' : ''}`);
    if (refonted) {
      toast.info(
        `“${t.name}” has its own fonts: ${refonted} line${refonted === 1 ? '' : 's'} switched to this design’s fonts, and Ready text now uses them (Ctrl+Z to undo).`,
        { duration: 7000 }
      );
    }
  };

  // Background = tint (from BackgroundTintControls) + artwork adjustments; each keeps the other.
  const handleTint = (tint, key) =>
    change((d) => {
      const adjust = Object.fromEntries(BG_ADJUST_KEYS.filter((k) => d.background?.[k] !== undefined).map((k) => [k, d.background[k]]));
      const next = tint ? { ...adjust, ...tint } : hasBackgroundAdjust(d.background) ? { ...adjust, tint_color: null } : null;
      return { ...d, background: next };
    }, key ? { key } : undefined);
  const handleAdjustBackground = (patch, key) =>
    change((d) => {
      const next = { tint_color: null, ...(d.background || {}), ...patch };
      return { ...d, background: next.tint_color || hasBackgroundAdjust(next) ? next : null };
    }, key ? { key } : undefined);

  const aiTarget = useMemo(() => {
    if (selected?.kind === 'text') return selected.item;
    return design.text_fields
      .filter((f) => !f.hidden)
      .reduce((best, f) => (!best || (f.text || '').length > (best.text || '').length ? f : best), null);
  }, [selected, design.text_fields]);

  const handleUseAi = (text) => {
    if (aiTarget) {
      patchItem(aiTarget.id, { text });
      setSelectedId(aiTarget.id);
      toast.success(`Wording added to “${aiTarget.label || 'Text'}”`);
    } else {
      handleAddText({ text, font_size: 24, line_width: 70, label: 'Wording' });
    }
    setAiDialogOpen(false);
    closePhoneSheet();
  };

  /** Glyphs panel: type into the selected text box at the cursor (same font) or add a new ornament box. */
  const handleInsertGlyph = (ch, family) => {
    const sel = selected?.kind === 'text' ? selected.item : null;
    if (sel && sel.font_family === family) {
      const text = sel.text || '';
      const caret = caretRef.current && caretRef.current.id === sel.id ? caretRef.current : { start: text.length, end: text.length };
      const next = text.slice(0, caret.start) + ch + text.slice(caret.end);
      patchItem(sel.id, { text: next });
      caretRef.current = { id: sel.id, start: caret.start, end: caret.start + ch.length };
      // keep the new glyph selected (tick = put the selection back in the in-place editor), so the
      // next alternate click swaps it again — like trying alternates in InDesign
      setCaret({ id: sel.id, start: caret.start, end: caret.start + ch.length, tick: Date.now() });
      return;
    }
    handleAddText({
      text: ch,
      font_family: family,
      font_size: 72,
      label: 'Ornament',
      ornament: true,
      color: sel?.color || '#7A5A1E',
      x: sel ? sel.x ?? 50 : 50,
      y: sel ? clamp((sel.y ?? 50) + 7, 3, 97) : 50,
    });
  };

  /** AI wording that comes back per text box: fill each box, add new boxes only for extra lines. */
  const handleUseAiStructured = (option) => {
    let lastId = null;
    const styled = (option.fields || []).some((f) => f.font_size || f.color || f.font_family || f.font_weight);
    change((d) => {
      let next = d;
      for (const f of option.fields || []) {
        if (findItem(next, f.id)) {
          const style = {};
          for (const k of ['font_size', 'color', 'font_family', 'font_weight']) if (f[k]) style[k] = f[k];
          next = updateItem(next, f.id, { text: f.text, hidden: false, ...style });
          lastId = f.id;
        }
      }
      const visible = next.text_fields.filter((f) => !f.hidden);
      let y = visible.length ? Math.max(...visible.map((f) => f.y ?? 50)) : 80;
      for (const line of option.extra || []) {
        if (next.text_fields.length >= MAX_TEXT_FIELDS) break;
        y = clamp(y + 4, 5, 95);
        const r = addText(next, { text: line, y, font_size: 18, line_width: 70, label: 'Extra line' });
        next = r.design;
        lastId = r.id;
      }
      // new sizes / extra lines: stack the lines again inside the design's text space (no overlaps)
      if (styled || (option.extra || []).length) next = restackText(next, currentTemplate?.text_area);
      return next;
    });
    const extras = (option.extra || []).length;
    toast.success(
      `${styled ? 'Design and wording applied' : 'Wording added to your text boxes'}${
        extras ? `, plus ${extras} new line${extras === 1 ? '' : 's'}` : ''
      } (Ctrl+Z to undo)`
    );
    if (lastId) setSelectedId(null);
    setAiDialogOpen(false);
    closePhoneSheet();
  };

  // ---- Multi-selection actions ----
  const selectedItems = () => selection.map((id) => findItem(designRef.current, id)).filter(Boolean);

  const nudge = (dx, dy) => {
    const items = selectedItems().filter((f) => !f.item.locked);
    if (!items.length) return;
    change(
      (d) =>
        items.reduce(
          (acc, { item }) =>
            updateItem(acc, item.id, {
              x: round(clamp((item.x ?? 50) + dx, 0, 100)),
              y: round(clamp((item.y ?? 50) + dy, 0, 100)),
            }),
          d
        ),
      { key: `nudge-${selection.join(',')}` }
    );
  };

  const removeSelection = () => {
    const items = selectedItems();
    if (!items.length) return;
    if (items.length === 1) return handleRemove(items[0].item.id);
    change((d) =>
      items.reduce((acc, found) => (isDeletable(found) ? removeItem(acc, found.item.id) : updateItem(acc, found.item.id, { hidden: true })), d)
    );
    const hidden = items.filter((f) => !isDeletable(f)).length;
    if (hidden) toast.info(`${hidden} template line${hidden === 1 ? ' was' : 's were'} hidden (template lines can be shown again from Layers).`);
    setSelection([]);
    return undefined;
  };

  const duplicateSelection = (offset = true, source = null) => {
    const items = source || selectedItems();
    if (!items.length) return;
    const texts = items.filter((f) => f.kind === 'text').length;
    if (designRef.current.text_fields.length + texts > MAX_TEXT_FIELDS) return toast.error('Too many text boxes.');
    if (designRef.current.elements.length + items.length - texts > MAX_ELEMENTS) return toast.error('Too many elements.');
    const created = [];
    change((d) => {
      let next = d;
      for (const { kind, item } of items) {
        const shifted = { ...item, x: clamp((item.x ?? 50) + (offset ? 3 : 0), 2, 98), y: clamp((item.y ?? 50) + (offset ? 3 : 0), 2, 98) };
        const { id: _drop, z: _z, ...rest } = shifted;
        const r = kind === 'text'
          ? addText(next, { ...rest, label: `${item.label || 'Text'} copy`, locked: false, hidden: false })
          : addElements(next, [{ ...rest, locked: false, hidden: false }]);
        next = r.design;
        created.push(r.id);
      }
      return next;
    });
    setSelection(created);
    return undefined;
  };

  const selectAll = () => {
    const d = designRef.current;
    setSelection([...d.elements, ...d.text_fields].filter((i) => !i.hidden && !i.locked).map((i) => i.id));
  };

  const copySelection = () => {
    const items = selectedItems();
    if (!items.length) return;
    clipboard.current = items.map(({ kind, item }) => ({ kind, item: { ...item } }));
    toast.success(`Copied ${items.length} item${items.length === 1 ? '' : 's'}`);
  };

  const pasteClipboard = () => {
    if (!clipboard.current.length) return;
    duplicateSelection(true, clipboard.current);
    // next paste lands a little further down-right
    clipboard.current = clipboard.current.map(({ kind, item }) => ({ kind, item: { ...item, x: (item.x ?? 50) + 3, y: (item.y ?? 50) + 3 } }));
  };

  const patchSelectedTexts = (makePatch) => {
    const items = selectedItems().filter((f) => f.kind === 'text');
    if (!items.length) return;
    change((d) => items.reduce((acc, { item }) => updateItem(acc, item.id, makePatch(item)), d));
  };

  const toggleLockSelection = () => {
    const items = selectedItems();
    if (!items.length) return;
    const lock = !items.every((f) => f.item.locked);
    change((d) => items.reduce((acc, { item }) => updateItem(acc, item.id, { locked: lock }), d));
  };

  // ---- Save ----
  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Please give your invitation a name before saving.');
      document.getElementById('invitation-name')?.focus();
      return false;
    }
    const current = designRef.current;
    const payload = {
      name: trimmed,
      text_fields: cleanForSave(current.text_fields),
      elements: cleanForSave(current.elements),
      background: current.background || null,
    };
    const templateChanged = Boolean(invitation && current.template_id && current.template_id !== invitation.template_id);
    if (templateChanged) payload.template_id = current.template_id;
    setSaving(true);
    try {
      if (invitation) {
        const { data } = await axios.put(`${API_URL}/invitations/${invitation.id}`, payload);
        setInvitation((prev) => ({
          ...prev,
          ...(data && typeof data === 'object' ? data : {}),
          ...(templateChanged ? { template_id: current.template_id } : {}),
        }));
        toast.success('Invitation saved');
      } else {
        const { data } = await axios.post(`${API_URL}/invitations`, { ...payload, template_id: current.template_id || templateId });
        toast.success('Invitation created and saved');
        setInvitation(data);
        loadedId.current = data.id;
        navigate(`/editor/invitation/${data.id}`, { replace: true });
      }
      setName(trimmed);
      setSaved({ snap: snapshot(current), name: trimmed, exists: true });
      breakMerge();
      return true;
    } catch (error) {
      if (error?.response?.status === 409 && templateChanged) {
        // Paid invitations can't switch design: keep the original background.
        toast.error(getErrorMessage(error, 'This invitation has been purchased, so its design can no longer be changed.'));
        change((d) => ({ ...d, template_id: invitation.template_id }));
        return false;
      }
      toast.error(getErrorMessage(error, 'We could not save your invitation. Please try again.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleRestored = (data) => {
    if (!data || typeof data !== 'object') return;
    const restored = {
      text_fields: Array.isArray(data.text_fields) ? data.text_fields : [],
      elements: Array.isArray(data.elements) ? data.elements : [],
      template_id: data.template_id || designRef.current.template_id,
      background: data.background && (data.background.tint_color || hasBackgroundAdjust(data.background)) ? data.background : null,
    };
    change(restored);
    setInvitation((prev) => ({ ...prev, ...data }));
    setName(data.name || name);
    setSaved({ snap: snapshot(restored), name: data.name || name, exists: true });
    setSelectedId(null);
    toast.success('Version restored');
  };

  // ---- Share, cart, download ----
  const handleCopyShare = async () => {
    if (!invitation?.share_link) {
      toast.info('Save your invitation first to get a share link');
      return;
    }
    const url = `${window.location.origin}/share/${invitation.share_link}`;
    if (await copyToClipboard(url)) toast.success('Share link copied to clipboard');
    else toast.info(`Share link: ${url}`, { duration: 10000 });
  };

  const handleAddToCart = async (thenCheckout = false) => {
    if (!invitation) {
      toast.info('Please save your invitation first');
      return;
    }
    setAddingToCart(true);
    try {
      await addToCart(invitation.id);
      setPurchase(null);
      if (thenCheckout) navigate('/cart');
      else toast.success('Added to cart', { action: { label: 'View cart', onClick: () => navigate('/cart') } });
    } catch (error) {
      if (error.response?.data?.detail === 'Item already in cart') {
        setPurchase(null);
        if (thenCheckout) navigate('/cart');
        else toast.info('This invitation is already in your cart', { action: { label: 'View cart', onClick: () => navigate('/cart') } });
      } else {
        toast.error(getErrorMessage(error, 'We could not add this to your cart.'));
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const locked = !isAdmin && !canDownload;

  const handleDownload = async (format, quality = 'standard') => {
    // SECURITY: downloads are generated server-side, which enforces payment.
    if (!format) {
      setPurchase({});
      return;
    }
    if (!invitation) {
      toast.error('Please save your invitation first');
      return;
    }
    if (locked) {
      setPurchase({});
      return;
    }
    if (dirty) {
      toast.info('You have unsaved changes. Save first so your download includes them.', {
        action: { label: 'Save now', onClick: () => handleSave() },
      });
      return;
    }
    setExporting(true);
    const label = `${format.toUpperCase()}${quality === 'print' ? ' (print quality)' : ''}`;
    const toastId = toast.loading(`Preparing your ${label}…`);
    try {
      const fileName = (name || 'Invitation').trim();
      const response = await axios.get(`${API_URL}/invitations/${invitation.id}/download-image`, {
        params: { quality },
        responseType: 'blob',
      });
      let blob = response.data;
      const type = blob.type || 'image/jpeg';
      if (format === 'pdf') {
        const url = URL.createObjectURL(blob);
        try {
          const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
          pdf.addImage(url, type.includes('png') ? 'PNG' : 'JPEG', 0, 0, 8.5, 11);
          pdf.save(`${fileName}.pdf`);
        } finally {
          URL.revokeObjectURL(url);
        }
      } else {
        const wanted = format === 'png' ? 'image/png' : 'image/jpeg';
        if (!type.includes(format === 'png' ? 'png' : 'jp')) blob = await convertBlob(blob, wanted);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${fileName}${quality === 'print' ? ' (print)' : ''}.${format}`;
        link.href = url;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      toast.success(`${label} downloaded`, { id: toastId });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 402) {
        toast.dismiss(toastId);
        setPurchase({ reason: 'Payment is required before you can download this invitation.' });
      } else if (status === 401) {
        toast.error('Please sign in again to download your invitation.', { id: toastId });
      } else {
        toast.error(`We could not generate your ${label}. Please try again or contact support.`, { id: toastId });
      }
    } finally {
      setExporting(false);
    }
  };

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(invitation ? '/dashboard' : '/templates');
  };

  // ---- Keyboard shortcuts ----
  const keyActions = useRef({});
  keyActions.current = {
    undo, redo, handleSave, nudge, mode, selection, selectedId, selected, removeSelection, duplicateSelection,
    selectAll, copySelection, pasteClipboard, patchSelectedTexts, toggleLockSelection, handleMove,
  };
  useEffect(() => {
    const onKey = (e) => {
      const a = keyActions.current;
      const mod = e.ctrlKey || e.metaKey;
      // e.code is the physical key, so shortcuts also work with a Hebrew/Yiddish keyboard layout
      // (there Ctrl+Z sends e.key "ז").
      const code = e.code || '';
      const letter = code.startsWith('Key') ? code.slice(3).toLowerCase() : (e.key || '').toLowerCase();
      if (mod && letter === 's') {
        e.preventDefault();
        a.handleSave();
        return;
      }
      if (e.defaultPrevented || isEditableTarget(e.target)) return;
      if (e.target.closest?.('[role="dialog"],[role="menu"],[role="listbox"]')) return;
      if (mod && letter === 'z') {
        e.preventDefault();
        if (e.shiftKey) a.redo();
        else a.undo();
        return;
      }
      if (mod && letter === 'y') {
        e.preventDefault();
        a.redo();
        return;
      }
      if (e.key === '?' || (mod && code === 'Slash')) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      if (a.mode !== 'advanced') {
        if (e.key === 'Escape' && a.selection.length) setSelection([]);
        return;
      }
      if (mod && letter === 'a') {
        e.preventDefault();
        a.selectAll();
        return;
      }
      if (mod && letter === 'v') {
        e.preventDefault();
        a.pasteClipboard();
        return;
      }
      if (!a.selection.length) return;
      if (e.key === 'Escape') {
        setSelection([]);
        if (document.activeElement?.dataset?.itemId) document.activeElement.blur();
        return;
      }
      if (mod && letter === 'c') {
        e.preventDefault();
        a.copySelection();
        return;
      }
      if (mod && letter === 'x') {
        e.preventDefault();
        a.copySelection();
        a.removeSelection();
        return;
      }
      if (mod && letter === 'd') {
        e.preventDefault();
        a.duplicateSelection();
        return;
      }
      if (mod && letter === 'l') {
        e.preventDefault();
        a.toggleLockSelection();
        return;
      }
      if (mod && !e.shiftKey && (letter === 'b' || letter === 'i' || letter === 'u')) {
        e.preventDefault();
        const prop = { b: ['font_weight', 'bold', 'normal'], i: ['font_style', 'italic', 'normal'], u: ['underline', true, false] }[letter];
        const items = a.selection.map((id) => findItem(designRef.current, id)).filter((f) => f?.kind === 'text');
        const on = !items.every((f) => f.item[prop[0]] === prop[1]);
        a.patchSelectedTexts(() => ({ [prop[0]]: on ? prop[1] : prop[2] }));
        return;
      }
      if (mod && e.shiftKey && (letter === 'l' || letter === 'e' || letter === 'r')) {
        e.preventDefault();
        a.patchSelectedTexts(() => ({ text_align: { l: 'left', e: 'center', r: 'right' }[letter] }));
        return;
      }
      if (mod && e.shiftKey && (code === 'Period' || code === 'Comma')) {
        e.preventDefault();
        const d = code === 'Period' ? 2 : -2;
        a.patchSelectedTexts((item) => ({ font_size: clamp((item.font_size || 24) + d, 4, 400) }));
        return;
      }
      if (mod && (code === 'BracketRight' || code === 'BracketLeft')) {
        e.preventDefault();
        if (a.selectedId) a.handleMove(a.selectedId, code === 'BracketRight' ? 1 : -1);
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        a.removeSelection();
        return;
      }
      const step = e.shiftKey ? 2 : e.altKey ? 0.05 : 0.25;
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (moves[e.key]) {
        e.preventDefault();
        a.nudge(...moves[e.key]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) return <PageLoader label="Loading your design" />;

  // The editor background already carries the server's watermark (editor-image.jpg): no second overlay.
  const showWatermark = false;
  const price = getTemplatePrice(currentTemplate);
  const requiredIds = (currentTemplate?.text_fields || []).filter((tf) => tf.is_required).map((tf) => tf.id);
  const layerCount = design.text_fields.length + design.elements.length;
  const advanced = mode === 'advanced';

  const canvasActions = {
    duplicate: handleDuplicate,
    move: (dir) => handleMove(selectedId, dir),
    toggleLock: () => selected && patchItem(selectedId, { locked: !selected.item.locked }),
    remove: () => handleRemove(),
    // several items selected
    duplicateMany: () => duplicateSelection(),
    removeMany: () => removeSelection(),
    lockMany: () => toggleLockSelection(),
    shortcuts: () => setShortcutsOpen(true),
  };
  // On-screen background: the light screen version (the print file is 13-75 MB). Customers get it with a
  // light "azamein" watermark baked in by the server (can't be saved clean from the page); admins and
  // paid invitations see the clean version.
  const cleanBackground = currentTemplate?.display_image || currentTemplate?.background_image;
  const screenBackground =
    !isAdmin && !canDownload && currentTemplate?.id && cleanBackground
      ? `${API_URL}/templates/${currentTemplate.id}/editor-image.jpg?v=${encodeURIComponent(String(cleanBackground).split('/').pop())}`
      : cleanBackground;
  // A box saved before field_input_id was kept in editor state: take it from the current template,
  // with the template box's input type (a legacy credit_card box has no field input).
  const templateBoxes = Object.fromEntries((currentTemplate?.text_fields || []).map((tf) => [tf.id, tf]));
  // top to bottom, with their current style (the AI can also re-design: sizes, colours, fonts)
  const aiFields = design.text_fields
    .filter((f) => !f.hidden)
    .slice()
    .sort((a, b) => (a.y ?? 50) - (b.y ?? 50))
    .map((f) => ({
      id: f.id,
      label: f.label || 'Text',
      text: f.text || '',
      font_size: f.font_size,
      color: f.color,
      font_family: f.font_family,
      font_weight: f.font_weight,
      // lets the server leave card fields (CVV, expiry, ...) out of the AI request
      field_input_id: f.field_input_id || f.answer_key || templateBoxes[f.id]?.field_input_id || undefined,
      input_type: f.input_type || templateBoxes[f.id]?.input_type || undefined,
      field_key: f.field_key || templateBoxes[f.id]?.field_key || undefined,
    }));
  const aiFonts = fonts.filter((f) => !f.decorative).map((f) => f.family);
  const aiTemplateIds = [...new Set([template?.id, currentTemplate?.id].filter(Boolean))];

  const openPreview = () => {
    setSimpleStep(2);
    setPreviewOpen(true);
  };

  const renderToolPanel = (id) => {
    switch (id) {
      case 'text':
        return <TextPanel onAddText={handleAddText} onApplyPreset={handleApplyPreset} textSelected={selected?.kind === 'text'} />;
      case 'designs':
        return (
          <TextDesignsPanel
            design={design}
            template={currentTemplate}
            fonts={fonts}
            designs={textDesignsFor(currentTemplate)}
            backgroundImage={currentTemplate?.thumbnail_image || screenBackground}
            activeId={textDesignId}
            onApply={(o) => {
              change((d) => ({ ...d, text_fields: o.fields }));
              setTextDesignId(o.id);
              toast.success(`${o.id === 'original' ? 'Original text design' : `“${o.name}”`} applied (Ctrl+Z to undo)`);
              closePhoneSheet();
            }}
          />
        );
      case 'colors':
        return (
          <ColorsPanel
            design={design}
            themeId={themeId}
            onApplyTheme={handleApplyTheme}
            onReplaceColor={(from, to, key) => change((d) => replaceColor(d, from, to), { key })}
          />
        );
      case 'ai':
        return <AiPanel occasion={template?.name} fields={aiFields} templateIds={aiTemplateIds} designFonts={aiFonts} onUseStructured={handleUseAiStructured} onUse={handleUseAi} targetLabel={aiTarget ? aiTarget.label || 'Selected text' : 'a new text box'} />;
      case 'elements':
        return <ElementsPanel onAdd={handleAddElements} />;
      case 'uploads':
        return (
          <UploadsPanel
            photos={[
              ...new Set([
                ...photos,
                ...design.elements.filter((e) => e.type === 'image' && e.src && !e.src.startsWith(ELEMENT_ART_PREFIX)).map((e) => e.src),
              ]),
            ]}
            onUploaded={(url) => setPhotos((p) => (p.includes(url) ? p : [url, ...p]))}
            onPlace={handlePlacePhoto}
          />
        );
      case 'background':
        return (
          <BackgroundPanel
            templates={categoryTemplates}
            loading={categoryLoading}
            currentId={design.template_id}
            currentTemplate={currentTemplate}
            onPick={handlePickTemplate}
            lockedReason={designLockedReason}
          />
        );
      case 'layers':
        return (
          <LayersPanel
            design={design}
            selectedId={selectedId}
            onSelect={(sid) => {
              setSelectedId(sid);
              closePhoneSheet();
            }}
            onPatch={(sid, patch) => patchItem(sid, patch)}
            onMove={handleMove}
            onDelete={handleRemove}
          />
        );
      default:
        return null;
    }
  };

  const purchaseCard = (
    <div className="gradient-border gb-cream rounded-[18px] p-5 flex flex-col gap-3">
      <div className="flex justify-between items-baseline gap-2">
        <h2 className="font-bold text-ink">Digital copy</h2>
        <span className="font-serif text-2xl font-semibold text-ink">{formatPrice(price)}</span>
      </div>
      <p className="text-sm text-[#4A4640] -mt-1">High-resolution and 4K print quality, watermark-free.</p>
      <button
        type="button"
        onClick={() => handleAddToCart(false)}
        disabled={!invitation || canDownload || isAdmin || addingToCart}
        className={cn('h-11 rounded-full bg-ink text-paper text-sm font-bold hover:bg-ink/90 disabled:opacity-50', FOCUS_RING)}
        data-testid="add-digital-sidebar-btn"
      >
        {canDownload ? 'Purchased' : addingToCart ? 'Adding…' : `Add to cart · ${formatPrice(price)}`}
      </button>
      {!invitation && <p className="text-xs text-[#6B665C]">Save your invitation to add it to your cart.</p>}
      {isAdmin && !canDownload && invitation && <p className="text-xs text-[#6B665C]">Admins can download without purchasing.</p>}
    </div>
  );

  // Glyphs: only in the text tools, for the selected text box (types at its cursor, same font).
  const glyphsPanel =
    selected?.kind === 'text' ? (
      <GlyphsPanel
        heading={false}
        fonts={fonts}
        defaultFamily={selected.item.font_family}
        onInsert={handleInsertGlyph}
        targetLabel={selected.item.label || 'Text'}
        selectedFamily={selected.item.font_family}
        selectedChar={(() => {
          // exactly one character selected in the selected text box -> show its alternates
          if (!caret || caret.id !== selected.item.id) return null;
          const chars = [...(selected.item.text || '').slice(caret.start, caret.end)];
          return chars.length === 1 ? chars[0] : null;
        })()}
      />
    ) : null;

  // Background effects: shown on the right when the background is clicked (nothing selected).
  const backgroundSettings = (
    <>
      <BackgroundTintControls background={design.background} onChange={handleTint} title="Background colour" />
      <BackgroundAdjustControls background={design.background} onChange={handleAdjustBackground} />
    </>
  );

  const properties = selected ? (
    selected.kind === 'text' ? (
      <TextProperties field={selected.item} fonts={fonts} onPatch={patchSelected} onCaret={reportCaret} glyphs={glyphsPanel} />
    ) : (
      <ElementProperties element={selected.item} onPatch={patchSelected} />
    )
  ) : (
    <NothingSelected background={backgroundSettings}>{purchaseCard}</NothingSelected>
  );

  const simplePanel = (
    <SimplePanel
      fields={design.text_fields}
      requiredIds={requiredIds}
      selectedId={selectedId}
      focusTick={focusTick}
      onText={(id, text) => patchItem(id, { text }, `${id}-text`)}
      onFocusField={setSelectedId}
      step={simpleStep}
      themeId={themeId}
      onApplyTheme={handleApplyTheme}
      onAi={() => setAiDialogOpen(true)}
      onPreview={openPreview}
      onAdvanced={() => setMode('advanced')}
      tintControls={<BackgroundTintControls compact background={design.background} onChange={handleTint} title="Background colour" />}
      designPicker={
        <BackgroundPanel
          variant="row"
          templates={categoryTemplates}
          loading={categoryLoading}
          currentId={design.template_id}
          currentTemplate={currentTemplate}
          onPick={handlePickTemplate}
          lockedReason={designLockedReason}
        />
      }
      className={isPhone ? '' : 'min-h-full'}
    />
  );

  const topBar = (
    <StudioTopBar
      isPhone={isPhone}
      onBack={handleBack}
      name={name}
      onNameChange={setName}
      saveState={saveState}
      subtitle={`${currentTemplate?.name || 'Template'} · 8.5 × 11 in`}
      mode={mode}
      onModeChange={setMode}
      canUndo={canUndo}
      canRedo={canRedo}
      onUndo={undo}
      onRedo={redo}
      canVersions={Boolean(invitation)}
      onVersions={() => setVersionsOpen(true)}
      onPreview={openPreview}
      invitation={invitation}
      onCopyShare={handleCopyShare}
      onSave={handleSave}
      saving={saving}
      download={{ locked, exporting, onDownload: handleDownload, price: formatPrice(price) }}
    />
  );

  const canvas = (
    <StudioCanvas
      design={design}
      selectedId={selectedId}
      selection={selection}
      onSelectionChange={setSelection}
      onTextEdit={(id, text) => patchItem(id, { text }, `${id}-text`)}
      onCaret={reportCaret}
      caret={caret}
      onSelect={(id) => {
        setSelectedId(id);
        if (!id) setPhoneQuick(null);
        else if (!advanced) setFocusTick((t) => t + 1);
      }}
      onChange={change}
      onGestureEnd={breakMerge}
      backgroundImage={screenBackground}
      backgroundTint={design.background}
      perspective={currentTemplate?.perspective}
      showWatermark={showWatermark}
      interactive={advanced}
      compact={isPhone}
      actions={canvasActions}
      footerNote={`8.5 × 11 in · ${layerCount} layer${layerCount === 1 ? '' : 's'}`}
    />
  );

  const dialogs = (
    <>
      <VersionsDialog
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        invitationId={invitation?.id}
        dirty={dirty}
        onRestored={handleRestored}
      />
      <PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        design={design}
        backgroundImage={screenBackground}
        perspective={currentTemplate?.perspective}
        showWatermark={!isAdmin && !canDownload}
        footer={
          locked ? (
            <button
              type="button"
              onClick={() => {
                setPreviewOpen(false);
                setPurchase({});
              }}
              className={cn('h-11 rounded-full bg-ink text-paper text-sm font-bold hover:bg-ink/90', FOCUS_RING)}
            >
              Remove watermark · {formatPrice(price)}
            </button>
          ) : null
        }
      />
      <PurchaseDialog
        open={Boolean(purchase)}
        onOpenChange={(o) => !o && setPurchase(null)}
        reason={purchase?.reason}
        price={price}
        canPurchase={Boolean(invitation)}
        busy={addingToCart}
        onAddToCart={() => handleAddToCart(false)}
        onBuyNow={() => handleAddToCart(true)}
      />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="bg-white rounded-[24px] border-0 p-6 max-w-[calc(100vw-24px)] sm:max-w-md max-h-[calc(100vh-24px)] overflow-y-auto">
          <DialogTitle className="font-serif text-2xl font-semibold">
            Write the wording <span className="italic text-logo-gradient">for me</span>
          </DialogTitle>
          <DialogDescription className="text-[#4A4640] -mt-2">
            Describe your simcha and pick a language. We&apos;ll suggest three versions.
          </DialogDescription>
          <AiPanel heading={false} occasion={template?.name} fields={aiFields} templateIds={aiTemplateIds} designFonts={aiFonts} onUseStructured={handleUseAiStructured} onUse={handleUseAi} targetLabel={aiTarget ? aiTarget.label || 'Selected text' : 'a new text box'} />
        </DialogContent>
      </Dialog>
    </>
  );

  const heading = (
    <h1 className="sr-only">{invitation ? `Edit ${name || 'invitation'}` : `Customize ${template?.name || 'template'}`}</h1>
  );

  // ---------------- Phone ----------------
  if (isPhone) {
    const sel = selected?.item;
    const textSel = selected?.kind === 'text';
    const chips = textSel
      ? [
          ['font', 'Font', 'Aa', { fontFamily: `'${sel.font_family}'` }],
          ['color', 'Colour', '●', { color: sel.color || '#1A1A1A' }],
          ['size', 'Size', String(sel.font_size || 24), { fontWeight: 800, fontSize: 15 }],
          ['style', 'Style', 'B', { fontWeight: 900 }],
          ['align', 'Align', <AlignCenter key="a" className="h-[18px] w-[18px]" />, {}],
          ['effects', 'Effects', <Sparkles key="e" className="h-[18px] w-[18px]" />, {}],
          ['glyphs', 'Glyphs', <Omega key="g" className="h-[18px] w-[18px]" />, {}],
        ]
      : [];

    return (
      <div className="h-[100dvh] flex flex-col bg-[#EDEAE3] overflow-hidden">
        {heading}
        {topBar}
        <main id="main-content" className="flex-1 min-h-0 flex flex-col">
          {canvas}
        </main>

        {advanced ? (
          <section
            aria-label="Edit selection"
            className="shrink-0 bg-white rounded-t-[22px] shadow-[0_-12px_30px_-18px_rgba(26,26,26,0.35)] pt-2.5 flex flex-col"
          >
            <div className="w-[38px] h-1 rounded-sm bg-[#DCD6C8] self-center mb-2" aria-hidden="true" />
            <div className="px-3.5 flex flex-col gap-2.5 pb-2.5">
              {sel ? (
                <>
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs text-[#6B665C] font-bold">{itemLabel(selected)}</span>
                      {textSel ? (
                        <span dir="auto" className="font-hebrew text-[17px] truncate">
                          {sel.text || '(empty)'}
                        </span>
                      ) : null}
                    </div>
                    {phoneQuick ? (
                      <button
                        type="button"
                        onClick={() => setPhoneQuick(null)}
                        aria-label="Close controls"
                        className={cn('h-9 w-9 rounded-full bg-cream flex items-center justify-center shrink-0', FOCUS_RING)}
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPhoneQuick(textSel ? 'text' : 'element')}
                        className={cn('h-9 px-3.5 rounded-full gradient-border-strong text-[13px] font-extrabold flex items-center gap-1.5 shrink-0', FOCUS_RING)}
                        data-testid="phone-edit-btn"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        {textSel ? 'Edit text' : 'Edit'}
                      </button>
                    )}
                  </div>
                  {textSel && (
                    <div className="flex gap-2 overflow-x-auto -mx-3.5 px-3.5 pb-1" role="group" aria-label="Quick controls">
                      {chips.map(([id, label, glyph, style]) => (
                        <QuickChip
                          key={id}
                          label={label}
                          glyph={glyph}
                          glyphStyle={style}
                          active={phoneQuick === id}
                          onClick={() => setPhoneQuick((q) => (q === id ? null : id))}
                          testId={`quick-${id}`}
                        />
                      ))}
                    </div>
                  )}
                  {phoneQuick && (
                    <div className="pt-1 max-h-[32vh] overflow-y-auto -mx-3.5 px-3.5" data-testid="phone-quick-panel">
                      {textSel ? (
                        <TextProperties field={sel} fonts={fonts} onPatch={patchSelected} onCaret={reportCaret} section={phoneQuick} glyphs={glyphsPanel} />
                      ) : (
                        <ElementProperties element={sel} onPatch={patchSelected} />
                      )}
                    </div>
                  )}
                </>
              ) : phoneQuick === 'background' ? (
                <>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-[#6B665C] font-bold">Background</span>
                    <button
                      type="button"
                      onClick={() => setPhoneQuick(null)}
                      aria-label="Close controls"
                      className={cn('h-9 w-9 rounded-full bg-cream flex items-center justify-center shrink-0', FOCUS_RING)}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="pt-1 max-h-[32vh] overflow-y-auto -mx-3.5 px-3.5 flex flex-col gap-4" data-testid="phone-background-panel">
                    {backgroundSettings}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between gap-3 py-1">
                  <p className="text-sm text-[#6B665C]">Tap something on the invitation to edit it, or add something below.</p>
                  <button
                    type="button"
                    onClick={() => setPhoneQuick('background')}
                    className={cn('h-9 px-3.5 rounded-full gradient-border-strong text-[13px] font-extrabold flex items-center gap-1.5 shrink-0', FOCUS_RING)}
                    data-testid="phone-background-btn"
                  >
                    <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Background
                  </button>
                </div>
              )}
            </div>
            <PhoneTabBar tab={phoneSheet} onTab={(id) => setPhoneSheet(id)} />
          </section>
        ) : (
          <section
            aria-label="Your details"
            className="shrink-0 max-h-[50vh] overflow-y-auto bg-white rounded-t-[22px] shadow-[0_-12px_30px_-18px_rgba(26,26,26,0.35)] px-4 pt-4 pb-6"
          >
            {simplePanel}
          </section>
        )}

        <Sheet open={Boolean(phoneSheet)} onOpenChange={(o) => !o && setPhoneSheet(null)} modal={false}>
          <SheetContent
            side="bottom"
            className="max-h-[64vh] overflow-y-auto rounded-t-[22px] bg-white p-5 pb-8 border-0 shadow-[0_-18px_40px_-18px_rgba(26,26,26,0.45)]"
            data-testid="phone-sheet"
          >
            <SheetTitle className="sr-only">{phoneSheet ? `${phoneSheet} tools` : 'Tools'}</SheetTitle>
            <SheetDescription className="sr-only">Tools for editing your invitation</SheetDescription>
            {phoneSheet && renderToolPanel(phoneSheet)}
            {phoneSheet === 'elements' && (
              <div className="mt-5">
                {renderToolPanel('uploads')}
              </div>
            )}
          </SheetContent>
        </Sheet>
        {dialogs}
      </div>
    );
  }

  // ---------------- Desktop ----------------
  return (
    <div className="h-screen flex flex-col bg-[#EDEAE3] overflow-hidden">
      {heading}
      {topBar}
      <div className="flex-1 min-h-0 flex">
        {advanced ? (
          <>
            <ToolRail tab={tab} onTab={setTab} />
            <aside aria-label="Tool options" className="w-[300px] shrink-0 bg-white border-r border-[#E6E1D6] px-[18px] py-5 overflow-y-auto" data-testid="tool-panel">
              {renderToolPanel(tab)}
            </aside>
          </>
        ) : (
          <aside aria-label="Your details" className="w-[400px] shrink-0 bg-white border-r border-[#E6E1D6] px-[26px] py-6 overflow-y-auto">
            {simplePanel}
          </aside>
        )}

        <main id="main-content" className="flex-1 min-w-0 flex flex-col relative">
          {canvas}
        </main>

        {advanced && (
          <aside aria-label="Properties" className="w-[318px] shrink-0 bg-white border-l border-[#E6E1D6] px-[18px] pt-[18px] pb-6 overflow-y-auto" data-testid="properties-panel">
            {properties}
          </aside>
        )}
      </div>
      {dialogs}
    </div>
  );
}
